import { useCallback, useMemo } from 'react';
import { UseFieldArrayAppend, useFormContext } from 'react-hook-form';
import {
  EventSearch,
  OnEventsSelectCallback,
} from 'src/components/Events/EventSearch';
import { useAppContext } from 'src/contexts/AppContext';
import { ModalBodyDataContainer } from 'src/modals/Modal/Modal.styled';
import { newBigIntId } from 'src/utils/idUtils';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import { DeliveryType, TicketGroupInput } from 'src/WebApiController';

import { PurchaseOrderFormDataType } from '../MultiplePurchaseWizard';

export function SearchEvent({
  append,
}: {
  append: UseFieldArrayAppend<PurchaseOrderFormDataType, 'purchaseOrders'>;
}) {
  const { loginContext } = useAppContext();
  const { setValue, watch } = useFormContext<PurchaseOrderFormDataType>();
  const purchaseOrder = watch(`purchaseOrders.${0}`);
  const ticketGroups = watch(`purchaseOrders.${0}.ticketGroups`);
  const currencyCode =
    purchaseOrder.currencyCode?.value ??
    loginContext?.user?.activeAccount?.currencyCode ??
    'USD';

  const ticketGroupFromPartialPurchase = watch(
    `purchaseOrders.${0}.ticketGroupFromPartialPurchaseOrder`
  );

  const selectedEventIds = useMemo(
    () =>
      ticketGroups
        ?.filter((tg) => tg.event?.virtId)
        .map((tg) => tg.event!.virtId),
    [ticketGroups]
  );

  const onEventsSelect = useCallback<OnEventsSelectCallback>(
    (data) => {
      const selectedViagogoVirtualIds = new Set<string>(
        ticketGroups
          .map((tg) => tg.event?.virtId)
          .filter((id): id is string => id != null)
      );

      const unselectedViagogoVirtualIds = new Set<string>();
      let newTicketGroups = [...(ticketGroups || [])];
      const startId = newBigIntId();

      data.forEach(({ event, isSelected, performer, venue }, i) => {
        if (isSelected) {
          // skip events that are already selected
          if (!selectedViagogoVirtualIds.has(event.virtId)) {
            if (ticketGroupFromPartialPurchase == null) {
              newTicketGroups.push({
                ticketGroupId: startId - i,
                deliveryType: posChangedField(DeliveryType.InApp),
                currencyCode: posChangedField(currencyCode),
                viagogoVirtualId: event.virtId,
                eventMappingId: event.mpId,
                viagogoEventId: event.viagId,
                posEventId: event.posId,
                event: event,
                expectedValue: posChangedField(null),
                faceValueCost: posField(null),
                row: posChangedField(null),
                section: posChangedField(null),
                listingNotes: posChangedField(null),
                numberOfTickets: posChangedField(null),
                unitCost: posChangedField(null),
                totalCost: posChangedField(null),
                performer: performer,
                venue: venue,
                taxPaid: posChangedField(1), // This is to turn off taxExempt by default
              } as TicketGroupInput);
            } else {
              newTicketGroups.push({
                ...ticketGroupFromPartialPurchase,
                ticketGroupId: startId - i,
                deliveryType: posChangedField(DeliveryType.InApp),
                currencyCode: posChangedField(currencyCode),
                viagogoVirtualId: event.virtId,
                eventMappingId: event.mpId,
                viagogoEventId: event.viagId,
                posEventId: event.posId,
                event: event,
                performer: performer,
                venue: venue,
                taxPaid: posChangedField(1), // This is to turn off taxExempt by default
              } as TicketGroupInput);
            }
          }
        } else {
          unselectedViagogoVirtualIds.add(event.virtId);
        }
      });

      if (unselectedViagogoVirtualIds.size > 0) {
        // If we unselect an event, that just mean we don't want to add this new event
        // this time, so only remove new ticket groups with this event (eventId is null)
        newTicketGroups = newTicketGroups?.filter(
          (tg) =>
            tg.numberOfTickets?.value ||
            (tg.event?.virtId &&
              !unselectedViagogoVirtualIds.has(tg.event.virtId))
        );
      }
      append({ ...purchaseOrder, ticketGroups: newTicketGroups });
    },
    [
      ticketGroups,
      append,
      purchaseOrder,
      ticketGroupFromPartialPurchase,
      currencyCode,
    ]
  );

  const onDeleteNumEventsSelected = useCallback(() => {
    setValue(`purchaseOrders.${0}.ticketGroups`, []);
  }, [setValue]);

  return (
    <>
      <ModalBodyDataContainer>
        <EventSearch
          onEventsSelect={onEventsSelect}
          selectedEvents={selectedEventIds}
          searchTextPrefill={undefined}
          numEventsSelected={ticketGroups.length}
          onDeleteNumEventsSelected={onDeleteNumEventsSelected}
        />
      </ModalBodyDataContainer>
    </>
  );
}
